import React, { useContext, useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import {
  Drawer,
  Button,
  Avatar,
  Row,
  Col,
  Layout,
  Form,
  Input,
  Select,
  List,
  message,
  Divider,
  Typography,
  Collapse,
  Tooltip,
  InputNumber,
} from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  LogoutOutlined,
  EditOutlined,
  SaveOutlined,
  UserAddOutlined,
  CheckOutlined,
  CloseOutlined,
  EuroCircleOutlined,
} from '@ant-design/icons';
import { StoreContext } from '../store/index';
import { delay, getDateFromTimestamp } from '../utils';
import { auth, fcmToken } from '../firebase';
import { LEVELS, POSITION, LANGUAGES } from '../constants';
import {
  updateUser,
  removeFriend,
  fetchUserByEmail,
  rejectRequest,
} from './api';
import { addFriend } from '../shifts/api';
import { addMessagingTokenToUser } from '../login/utils';
import PlayersList from '../components/PlayersList';
import TeamList from '../components/TeamList';
import BallIcon from '../components/ballIcon';
import FootIcon from '../components/footIcon';

const Profile = () => {
  const store = useContext(StoreContext);
  const {
    user: [user, setUser, setUpdateUser],
    isUserLoading: [isUserLoading, setIsUserLoading],
  } = store;
  const { Option } = Select;
  const { Content } = Layout;
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    nickname,
    age,
    position,
    languageSelected,
    level,
    email,
    phoneNumber,
    role,
    photoURL,
    requests,
    createdGames,
    games,
    goals,
    absent,
    tokens = 0,
    friends,
    friendsList = [],
  } = user;

  const validate = async () => {
    try {
      const values = await form.validateFields();
      return values;
    } catch (errorInfo) {
      return null;
    }
  };

  const onFormSubmit = async () => {
    const values = await validate();
    if (values) {
      const res = await updateUser(user.uid, values, setLoading);
      if (res) {
        setIsEditing(false);
      }
    }
    await delay(2000);
    setUpdateUser(true);
  };

  const initFormValues = () => {
    form.setFieldsValue({
      nickname,
      age,
      position,
      languageSelected: languageSelected || 'es',
      level,
      photoURL,
    });
  };

  const handleRemoveFriend = (friendEmail, setIsLoading) => {
    removeFriend(user.uid, user.friendsIds, friendEmail, setIsLoading).then(
      () => setUpdateUser(true),
    );
  };

  const handleAddFriend = async friend => {
    const friendEmail = `${friend}@gmail.com`;
    const f = await fetchUserByEmail(friendEmail, setLoading);
    if (user.friendsIds.find(id => id === friendEmail)) {
      return message.error(
        formatMessage({ id: 'profile.add.friend.validation' }),
      );
    }
    if (f.length > 0) {
      addFriend(user.uid, user.friendsIds, friendEmail, setLoading).then(() =>
        setUpdateUser(true),
      );
    } else message.error(formatMessage({ id: 'profile.add.friend.error' }));
    return true;
  };

  const getDefaultLanguage = () => {
    if (languageSelected?.includes('es')) {
      return LANGUAGES.SPANISH;
    }
    if (languageSelected?.includes('ar')) {
      return LANGUAGES.ARABIC;
    }
    return LANGUAGES.ENGLISH;
  };

  useEffect(() => {
    initFormValues();
  }, []);

  const handleViewGame = id => {
    window.open(`https://www.gamebooking.top/shifts?${id}`, '_self');
  };
  const handleRejectRequest = id => {
    rejectRequest(id);
    setUpdateUser(true);
  };

  const handleSuscribe = () => {
    addMessagingTokenToUser(fcmToken, user.uid);
  };

  return (
    <Layout style={{ padding: '8px' }}>
      <Content
        style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '5px',
        }}>
        <Row justify="end">
          <Button
            type="primary"
            icon={<EditOutlined />}
            loading={loading}
            onClick={() => {
              setIsEditing(!isEditing);
              initFormValues();
            }}>
            {formatMessage({ id: 'common.actions.edit' })}
          </Button>
        </Row>
        <Row justify="center">
          <Col span={24}>
            <Form
              name="profile"
              form={form}
              onFinish={onFormSubmit}
              layout="vertical">
              <Row justify="center">
                <Typography.Text strong>{nickname}</Typography.Text>
              </Row>
              <Row justify="center" style={{ marginTop: '8px' }}>
                <Avatar size={100} src={photoURL} />
              </Row>
              <Row justify="center" style={{ marginTop: '8px' }}>
                <Typography.Text strong>{email}</Typography.Text>
              </Row>
              <Row justify="center">
                <Typography.Text>{phoneNumber}</Typography.Text>
              </Row>
              <Divider />
              <Row justify="center" gutter={16}>
                <Col>
                  <Typography.Text>{`Edad: ${age}`}</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text>{`Posición: ${position}`}</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text>{`Nivel: ${level}`}</Typography.Text>
                </Col>
              </Row>
              <Row justify="center" align="middle" gutter={16}>
                <Col>
                  <Typography.Text>
                    {`Organizados: ${createdGames || 0}`}
                  </Typography.Text>
                </Col>
                <Col>
                  <Tooltip title={formatMessage({ id: 'player.props.games' })}>
                    <Row>
                      <FootIcon size="20px" />
                      <Typography.Text>{`${games || 0}`}</Typography.Text>
                    </Row>
                  </Tooltip>
                </Col>
                <Col>
                  <Tooltip title={formatMessage({ id: 'player.props.goals' })}>
                    <Row>
                      <BallIcon size="16px" />
                      <Typography.Text>{` ${goals || 0}`}</Typography.Text>
                    </Row>
                  </Tooltip>
                </Col>
                <Col>
                  <Typography.Text>{`Ausencias: ${absent}`}</Typography.Text>
                </Col>
                <Col>
                  <Row>
                    <EuroCircleOutlined
                      style={{ fontSize: '18px', marginRight: '2px' }}
                    />
                    <Typography.Text>{tokens}</Typography.Text>
                  </Row>
                </Col>
              </Row>
              {isEditing && (
                <>
                  <Divider />
                  <Form.Item
                    name="nickname"
                    label={formatMessage({ id: 'player.props.name' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese su nombre.',
                      },
                    ]}>
                    <Input placeholder="Nombre" readOnly={!isEditing} />
                  </Form.Item>
                  <Form.Item
                    name="photoURL"
                    label={formatMessage({ id: 'player.props.picture' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese un link a su foto.',
                      },
                    ]}>
                    <Input placeholder="Picture Link" readOnly={!isEditing} />
                  </Form.Item>
                  <Form.Item
                    name="age"
                    label={formatMessage({ id: 'player.props.age' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese su edad.',
                      },
                    ]}>
                    <InputNumber
                      className="width100"
                      placeholder="Edad"
                      defaultValue={age}
                      min={0}
                      max={99}
                      readOnly={!isEditing}
                    />
                  </Form.Item>
                  <Form.Item
                    name="languageSelected"
                    label={formatMessage({ id: 'player.props.language' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese su idioma.',
                      },
                    ]}>
                    <Select
                      disabled={!isEditing}
                      defaultValue={getDefaultLanguage()}
                      onChange={value =>
                        form.setFieldsValue({
                          languageSelected: value,
                        })
                      }>
                      <Option key={LANGUAGES.SPANISH}>
                        {formatMessage({ id: 'language.spanish' })}
                      </Option>
                      <Option key={LANGUAGES.ENGLISH}>
                        {formatMessage({ id: 'language.english' })}
                      </Option>
                      <Option key={LANGUAGES.ARABIC}>
                        {formatMessage({ id: 'language.arabic' })}
                      </Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="position"
                    label={formatMessage({ id: 'player.props.position' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese su posición.',
                      },
                    ]}>
                    <Select
                      disabled={!isEditing}
                      defaultValue={POSITION.ANY}
                      onChange={value =>
                        form.setFieldsValue({
                          position: value,
                        })
                      }>
                      <Option key={POSITION.ANY}>{POSITION.ANY}</Option>
                      <Option key={POSITION.GOALKEEPER}>
                        {POSITION.GOALKEEPER}
                      </Option>
                      <Option key={POSITION.DEFENCE}>{POSITION.DEFENCE}</Option>
                      <Option key={POSITION.MIDFIELD}>
                        {POSITION.MIDFIELD}
                      </Option>
                      <Option key={POSITION.FOWARD}>{POSITION.FOWARD}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="level"
                    label={formatMessage({ id: 'player.props.level' })}
                    rules={[
                      {
                        required: false,
                        message: 'Ingrese su nivel.',
                      },
                    ]}>
                    <Select
                      disabled={!isEditing}
                      defaultValue={LEVELS.MEDIUM}
                      onChange={value =>
                        form.setFieldsValue({
                          level: value,
                        })
                      }>
                      <Option key={LEVELS.BASIC}>{LEVELS.BASIC}</Option>
                      <Option key={LEVELS.MEDIUM}>{LEVELS.MEDIUM}</Option>
                      <Option key={LEVELS.ADVANCE}>{LEVELS.ADVANCE}</Option>
                    </Select>
                  </Form.Item>
                  <Button
                    className="width100"
                    type="primary"
                    loading={loading}
                    icon={<SaveOutlined />}
                    htmlType="submit">
                    {formatMessage({ id: 'common.save' })}
                  </Button>
                </>
              )}

              <Divider />
              <Form.Item>
                <Input addonAfter="@gmail.com" placeholder="Email" />
              </Form.Item>
              <Button
                className="width100"
                type="primary"
                loading={loading}
                icon={<UserAddOutlined />}
                onClick={() =>
                  handleAddFriend(form.getFieldValue('friendEmail'))
                }>
                {formatMessage({ id: 'profile.add.friend' })}
              </Button>
              <Divider />
              <PlayersList
                header={formatMessage({ id: 'player.props.friends' })}
                startOpen={false}
                dataSource={friends}
                handleRemoveFriend={handleRemoveFriend}
              />
              <TeamList
                header={formatMessage({ id: 'player.props.friends.list' })}
                dataSource={friendsList}
              />
              <Divider />
              {!loading && (
                <Collapse
                  key="friends"
                  style={{ marginTop: '2px' }}
                  className="width100"
                  items={[
                    {
                      key: 'requestsPanel',
                      label: 'Partidos',
                      children: (
                        <List
                          size="small"
                          itemLayout="vertical"
                          loading={loading}
                          dataSource={requests}
                          renderItem={request => (
                            <List.Item
                              style={{ padding: '2px', margin: '2px' }}
                              key={request.id}>
                              <List.Item.Meta
                                avatar={<Avatar src={request.photoURL} />}
                                title={
                                  <Row justify="space-between">
                                    <Col>{request.startDate}</Col>
                                    <Col>
                                      <Row>
                                        <Col xs={12}>
                                          <Button
                                            key={request.id}
                                            type="primary"
                                            loading={loading}
                                            onClick={() =>
                                              handleViewGame(request.shiftId)
                                            }>
                                            {formatMessage({
                                              id: 'profile.request.view',
                                            })}
                                          </Button>
                                        </Col>
                                        <Col xs={8} offset={4}>
                                          <Button
                                            key={request.id}
                                            danger
                                            loading={loading}
                                            icon={<CloseOutlined />}
                                            onClick={() =>
                                              handleRejectRequest(request.id)
                                            }
                                          />
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                }
                                description={
                                  <Col>
                                    <Row>{`Lugar: ${request.placeName}`}</Row>
                                    <Row>{`Te invito: ${request.from}`}</Row>
                                  </Col>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                  ]}
                />
              )}
              <Divider />
              <Button
                className="width100"
                type="primary"
                loading={loading}
                icon={<LogoutOutlined />}
                onClick={() => auth.signOut()}>
                {formatMessage({ id: 'common.logout' })}
              </Button>
            </Form>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Profile;
